import React from 'react';
import { Card } from '../../ui/card';
import { Music2, Users, Calendar, Mail, Layers, Image, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const getGrowthIcon = (growth) => {
  if (growth > 0) return <ArrowUp className="w-4 h-4" />;
  if (growth < 0) return <ArrowDown className="w-4 h-4" />;
  return <Minus className="w-4 h-4" />;
};

const getGrowthColor = (growth) => {
  if (growth > 0) return 'text-green-400';
  if (growth < 0) return 'text-red-400';
  return 'text-gray-400';
};

const calculateGrowth = (current, previous) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous * 100).toFixed(1);
};

const StatCard = ({ label, value, icon: Icon, color, growth, growthLabel, onClick }) => (
  <Card onClick={onClick}
    className={`bg-gradient-to-br ${color} p-6 cursor-pointer hover:scale-105 transition-all duration-300 hover:shadow-lg`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className="text-3xl font-bold text-white mt-2">{value}</p>
        {growth !== undefined ? (
          <div className={`flex items-center gap-1 mt-2 text-sm ${getGrowthColor(growth)}`}>
            {getGrowthIcon(growth)}
            <span>{Math.abs(growth)}% this month</span>
          </div>
        ) : growthLabel ? (
          <p className="text-xs mt-2" style={{ color: 'inherit' }}>{growthLabel}</p>
        ) : null}
      </div>
      <div className="p-4 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
        <Icon className="w-8 h-8" />
      </div>
    </div>
  </Card>
);

export const DashboardStats = ({ analytics, navigate }) => {
  const releasesGrowth = calculateGrowth(
    analytics.monthly_growth?.releases?.current || 0,
    analytics.monthly_growth?.releases?.previous || 0
  );
  const eventsGrowth = calculateGrowth(
    analytics.monthly_growth?.events?.current || 0,
    analytics.monthly_growth?.events?.previous || 0
  );
  const contactsGrowth = calculateGrowth(
    analytics.monthly_growth?.contacts?.current || 0,
    analytics.monthly_growth?.contacts?.previous || 0
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <StatCard label="Total Releases" value={analytics.total_releases} icon={Music2}
        color="from-orange-500/20 to-orange-600/20 border-orange-500/30 hover:border-orange-500 hover:shadow-orange-500/50"
        growth={releasesGrowth} onClick={() => navigate('/admin/releases')} />
      <StatCard label="Total Artists" value={analytics.total_artists} icon={Users}
        color="from-green-500/20 to-green-600/20 border-green-500/30 hover:border-green-500 hover:shadow-green-500/50"
        growthLabel="Click to manage →" onClick={() => navigate('/admin/artists')} />
      <StatCard label="Upcoming Events" value={analytics.total_events} icon={Calendar}
        color="from-orange-500/20 to-yellow-500/20 border-orange-500/30 hover:border-orange-500 hover:shadow-orange-500/50"
        growth={eventsGrowth} onClick={() => navigate('/admin/events')} />
      <StatCard label="Contact Messages" value={analytics.total_contacts} icon={Mail}
        color="from-green-500/20 to-teal-500/20 border-green-500/30 hover:border-green-500 hover:shadow-green-500/50"
        growth={contactsGrowth} onClick={() => navigate('/admin/contacts')} />
      <StatCard label="Partners" value={analytics.total_partners} icon={Layers}
        color="from-purple-500/20 to-purple-600/20 border-purple-500/30 hover:border-purple-500 hover:shadow-purple-500/50"
        growthLabel="Click to manage →" onClick={() => navigate('/admin/partners')} />
      <StatCard label="Hero Slides" value={analytics.total_hero_slides} icon={Image}
        color="from-cyan-500/20 to-cyan-600/20 border-cyan-500/30 hover:border-cyan-500 hover:shadow-cyan-500/50"
        growthLabel="Click to manage →" onClick={() => navigate('/admin/hero-slides')} />
    </div>
  );
};
